import { useState, useEffect, type ReactNode } from "react"
import { useDispatch } from "react-redux"
import axios from "axios"
import type { AppDispatch } from "@/store"
import type { AuthUserResponse } from "@/modules/auth/entities/AuthUserResponse"
import { restoreAuth } from "@/modules/auth/store/authSlice"

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string

/**
 * Verifies the user's session on app startup by checking httpOnly cookies.
 *
 * Uses raw axios (not apiClient) to avoid triggering the response interceptor's
 * logout broadcast — on cold start there's no "session to lose", so a 401 here
 * is normal and should not affect other tabs.
 */
async function verifySession(): Promise<AuthUserResponse | null> {
  try {
    const res = await axios.get<AuthUserResponse>(
      `${API_BASE_URL}/auth/profile`,
      { withCredentials: true },
    )
    return res.data
  } catch {
    // Access token missing or expired — attempt a silent refresh
    try {
      await axios.post(
        `${API_BASE_URL}/auth/refresh`,
        {},
        { withCredentials: true },
      )
      // Refresh succeeded (new access_token cookie set) — retry profile
      const res = await axios.get<AuthUserResponse>(
        `${API_BASE_URL}/auth/profile`,
        { withCredentials: true },
      )
      return res.data
    } catch {
      // No valid session — user will see the login page
      return null
    }
  }
}

interface SessionInitializerProps {
  children: ReactNode
  fallback?: ReactNode
}

/**
 * Replaces PersistGate — checks if the user has a valid cookie-based session
 * and restores Redux auth state from the server response.
 *
 * Nothing is read from or written to localStorage.
 */
export function SessionInitializer({ children, fallback }: SessionInitializerProps) {
  const [isReady, setIsReady] = useState(false)
  const dispatch = useDispatch<AppDispatch>()

  useEffect(() => {
    verifySession()
      .then((profile) => {
        if (profile) {
          dispatch(
            restoreAuth({
              user: {
                user: {
                  id: profile.id,
                  firstName: profile.firstName,
                  lastName: profile.lastName,
                  email: profile.email,
                  status: profile.status,
                  createdAt: profile.createdAt,
                  lastLoginAt: profile.lastLoginAt,
                  needsLegalConsent: profile.needsLegalConsent,
                  organizationRole: profile.organizationRole,
                  organizationRoles: profile.organizationRoles,
                },
                roles: profile.roles ?? [],
                permissions: profile.permissions ?? [],
                currentOrganizationId: profile.currentOrganizationId ?? null,
                organizations: profile.organizations ?? [],
              },
              loginTimestamp: Date.now(),
            }),
          )
        }
      })
      .finally(() => setIsReady(true))
  }, [dispatch])

  if (!isReady) {
    return (
      <>
        {fallback ?? (
          <div className="flex items-center justify-center min-h-screen">
            Loading...
          </div>
        )}
      </>
    )
  }

  return <>{children}</>
}
